import { useEffect, useMemo } from "react";
import { useFilterAllTimeEntriesQuery } from "app/api/absence";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectSearch, selectCurrentFilters, setSearch, setCurrentFilters, DEFAULT_FILTERS, TimeEntryFilters } from "pages/timeManagement/timeManagementSlice";
import { useDebounce } from "../../hooks/useDebounce";
import { addDays, setDay } from "date-fns";
import { ScheduleScale } from "features/schedule-view/ScheduleContext";

export type Filters = Omit<TimeEntryFilters, "fromDateIso" | "toDateIso"> & {
  fromDate: Date;
  toDate: Date;
};

type TimeEntrySearchOptions = { view: "LIST" | ScheduleScale };

export const useTimeEntrySearch = ({ view }: TimeEntrySearchOptions) => {
  const dispatch = useAppDispatch();

  const search = useAppSelector(selectSearch);
  const { fromDateIso, toDateIso, ...filters } = useAppSelector(selectCurrentFilters);

  const debouncedSearch = useDebounce(search, { delayMs: 500 });

  const activeFiltersCount = useMemo(
    () => Object.entries(filters).filter(([key, value]) => {
      const defaultValue = DEFAULT_FILTERS[key as keyof TimeEntryFilters];
      return JSON.stringify(value) !== JSON.stringify(defaultValue);
    }).length,
    [filters]
  );

  const { data, isFetching, isLoading } = useFilterAllTimeEntriesQuery({
    searchQuery: debouncedSearch,
    fromDate: fromDateIso,
    toDate: addDays(new Date(toDateIso), 1).toISOString(), // Include the entire toDate by adding one day
    ...filters,
  });

  const updateSearch = (search: string) => {
    dispatch(setSearch(search));
  };

  const updateFilters = ({ fromDate, toDate, ...filters }: Filters) => {
    // Align fromDate and toDate to week start/end in weekly views
    const adjustedFromDate =
      view === "WEEKS" || view === "WORKWEEKS"
        ? setDay(fromDate, 1, { weekStartsOn: 1 })
        : fromDate;
    const adjustedToDate =
      view === "WEEKS" || view === "WORKWEEKS"
        ? setDay(toDate, 0, { weekStartsOn: 1 })
        : toDate;

    dispatch(setCurrentFilters({
      fromDateIso: adjustedFromDate.toISOString(),
      toDateIso: adjustedToDate.toISOString(),
      ...filters
    }));
  };

  const resetFilters = () => {
    dispatch(setCurrentFilters(DEFAULT_FILTERS));
  }

  // Ensure that fromDate and toDate are aligned to week start/end in weekly views
  useEffect(() => {
    if (view === "WEEKS" || view === "WORKWEEKS") {
      updateFilters({ fromDate: new Date(fromDateIso), toDate: new Date(toDateIso), ...filters });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  return {
    search,
    filters: { fromDate: new Date(fromDateIso), toDate: new Date(toDateIso), ...filters },
    activeFiltersCount,
    data,
    isFetching,
    isLoading,
    setSearch: updateSearch,
    setFilters: updateFilters,
    resetFilters,
  };
};
